import { useMemo } from 'react';
import { flattenListingGroup } from 'src/modals/GroupListings/components/groupingUtils';
import {
  AutoPricingResultType,
  GroupType,
  IListingGroupItem,
  Listing,
  ListingBroadcastStatus,
  ListingGroup,
  ListingStatus,
} from 'src/WebApiController';

import { ListingWithEvent } from '../ListingTable.types';

export interface ListingGroupMetrics {
  groupName: string;
  allListings: IListingGroupItem[];
  metrics: {
    activeCount: number;
    soldCount: number;
    priceConflictCount: number;
    isAutoPricedCount: number;
    sections: string[];
  };
}

export const useListingGroupMetrics = (
  data: (ListingWithEvent | null)[] | undefined
) => {
  return useMemo(() => {
    const map = new Map<string, ListingGroupMetrics>();

    // Pre-calculate metrics for all listing groups in the data
    data?.forEach((d) => {
      // Only process listing groups
      if (!d?.isLtGrp || !d.listing) {
        return;
      }

      const listingGroup = d.listing as ListingGroup;
      const groupId = String(d.listing.id);

      // Flatten all listings in the group (including nested groups)
      const allListings = flattenListingGroup(listingGroup);

      // Aggregate all metrics in a single pass
      const metrics = allListings.reduce(
        (acc, listing) => {
          const typedListing = listing as Listing;

          if (typedListing?.brdcstStatus === ListingBroadcastStatus.Broadcast) {
            acc.activeCount++;
          }

          if ((listing as ListingGroup)?.groupType === GroupType.FullySold) {
            acc.soldCount++;
          }

          if (
            typedListing?.isAutoPrc &&
            typedListing?.autoPrcResult ===
              AutoPricingResultType.PriceHitHardFloor
          ) {
            acc.priceConflictCount++;
          }

          if (typedListing?.isAutoPrc) {
            acc.isAutoPricedCount++;
          }

          if (typedListing.status === ListingStatus.FullySold) {
            acc.soldCount++;
          }

          const section = listing?.seating?.section;
          if (section) {
            acc.sectionsSet.add(section);
          }

          return acc;
        },
        {
          activeCount: 0,
          soldCount: 0,
          priceConflictCount: 0,
          isAutoPricedCount: 0,
          sectionsSet: new Set<string>(),
        }
      );

      map.set(groupId, {
        groupName: listingGroup.groupName || `Group ${groupId}`,
        allListings,
        metrics: {
          activeCount: metrics.activeCount,
          soldCount: metrics.soldCount,
          priceConflictCount: metrics.priceConflictCount,
          isAutoPricedCount: metrics.isAutoPricedCount,
          sections: Array.from(metrics.sectionsSet),
        },
      });
    });

    return map;
  }, [data]);
};
